const AI_DRAFT_CACHE_KEY_PREFIX = 'zrm_ai_property_fields';

/**
 * Only bounds how long the drafting screen keeps showing. Restoring a finished draft is not limited
 * by it, so one started long ago still comes back once the server returns it.
 */
export const AI_DRAFT_WAIT_WINDOW_MS = 30 * 60 * 1000;

export type AiAmenityField = {
  confidence: number;
  field: string;
  value: boolean;
};

/**
 * The app's own shape for an AI draft. The two endpoints return this under two generated names, so
 * callers normalise into this rather than passing a particular DTO around.
 */
export type AiDraftFields = {
  description: { confidence: number; value: string };
  amenityFields: Array<AiAmenityField>;
};

export type CachedAiDraft = {
  startedAtMs: number;
  /** Absent while the draft is still generating. */
  fields?: AiDraftFields;
};

/** Keyed per listing so drafting a second property cannot overwrite the first one's draft. */
const cacheKey = (listingAlias: string) => `${AI_DRAFT_CACHE_KEY_PREFIX}:${listingAlias}`;

function readCache(listingAlias: string): CachedAiDraft | null {
  if (typeof window === 'undefined' || !listingAlias) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(listingAlias));
    return raw ? (JSON.parse(raw) as CachedAiDraft) : null;
  } catch {
    return null;
  }
}

function writeCache(listingAlias: string, draft: CachedAiDraft): void {
  if (typeof window === 'undefined' || !listingAlias) return;
  try {
    sessionStorage.setItem(cacheKey(listingAlias), JSON.stringify(draft));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

/** Records that a draft was requested, so a reload can tell "still generating" from "never ran". */
export function markAiDraftStarted(listingAlias: string, startedAtMs: number): void {
  if (readCache(listingAlias)?.fields) return;

  writeCache(listingAlias, { startedAtMs });
}

export function cacheAiDraftFields(listingAlias: string, fields: AiDraftFields, startedAtMs: number): void {
  writeCache(listingAlias, { startedAtMs, fields });
}

export function getCachedAiDraft(listingAlias: string): CachedAiDraft | null {
  return readCache(listingAlias);
}
